import { HttpClient } from '#client/http/index'
import {
  AuthUser,
  RegisterParams,
  ResendEmailParams,
  EmailVerificationKey,
  VerifyEmailParams,
  ResetPasswordParams,
  DeregisterQuestion,
  DeregisterParams,
  LoginResult,
  Passkey,
  PasskeyRegistrationOptions,
  PasskeyAuthenticationOptions,
  PasskeyCredential,
} from '#client/resources/index'
import { ApiResponse } from '#client/resources/types'

export const get = async ({ httpClient }: { httpClient: HttpClient }) => {
  const { data }: ApiResponse<AuthUser> = await httpClient.get('/auth/user')
  return data
}

export const login = async (
  { httpClient }: { httpClient: HttpClient },
  email: string,
  password: string,
  remember: 0 | 1 = 1,
  // method=sms（SP）の場合、サーバはメールコードを送らず pending セッションのみ作成する
  // （コードの送信・検証はクライアント側の Firebase Phone Auth が担う）
  method: 'email' | 'sms' = 'email',
) => {
  // 認証情報が正しい場合でも即ログインは確立せず、2段階認証が要求される
  const { data }: ApiResponse<LoginResult> = await httpClient.post(
    '/auth/user/login',
    {
      email,
      password,
      remember,
      method,
    },
  )
  return data
}

export const verifyLoginTwoStepCode = async (
  { httpClient }: { httpClient: HttpClient },
  code: string,
) => {
  // コード検証成功時にログインが確立され、認証済みユーザーが返る
  const { data }: ApiResponse<AuthUser> = await httpClient.post(
    '/auth/user/login/2fa/verify',
    { code },
  )
  return data
}

export const resendLoginTwoStepCode = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  await httpClient.post('/auth/user/login/2fa/resend')
}

export const verifyLoginSmsToken = async (
  { httpClient }: { httpClient: HttpClient },
  idToken: string,
) => {
  // SMS（SP）2段階認証。クライアント側 Firebase が発行した ID トークンを検証し、
  // 検証成功時にログインが確立され、認証済みユーザーが返る。
  // SMS 2FA は登録済み電話番号を持つユーザー限定のため、番号の保存（savePhone）は行わない。
  const { data }: ApiResponse<AuthUser> = await httpClient.post(
    '/auth/user/login/2fa/sms/verify',
    { idToken },
  )
  return data
}

export const logout = async ({ httpClient }: { httpClient: HttpClient }) => {
  await httpClient.delete('/auth/user/logout')
}

export const register = async (
  { httpClient }: { httpClient: HttpClient },
  {
    email,
    password,
    passwordConfirmation,
    campaignCode,
    optIns,
    referralSubjectCode,
    socialSignupToken,
    utmSource,
    utmMedium,
    utmCampaign,
    intended,
  }: RegisterParams,
) => {
  const { data }: ApiResponse<AuthUser> = await httpClient.post(
    '/auth/user/register',
    {
      email,
      password,
      password_confirmation: passwordConfirmation,
      campaignCode,
      optIns,
      referralSubjectCode: referralSubjectCode ?? null,
      socialSignupToken: socialSignupToken ?? null,
      utmSource,
      utmMedium,
      utmCampaign,
      intended,
    },
  )
  return data
}

export const resendEmail = async (
  { httpClient }: { httpClient: HttpClient },
  params?: ResendEmailParams,
) => {
  return await httpClient.post('/auth/user/email/resend', params)
}

export const verifyEmail = async (
  { httpClient }: { httpClient: HttpClient },
  verificationKey: EmailVerificationKey,
  params: VerifyEmailParams,
) => {
  return await httpClient.get(`/auth/user/email/verify/${verificationKey}`, {
    params,
  })
}

export const passwordResetEmail = async (
  { httpClient }: { httpClient: HttpClient },
  email: string,
) => {
  return await httpClient.post(`/auth/user/password/email`, { email })
}

export const passwordReset = async (
  { httpClient }: { httpClient: HttpClient },
  params: ResetPasswordParams,
) => {
  return await httpClient.post(`/auth/user/password/reset`, {
    ...params,
    password_confirmation: params.passwordConfirmation,
  })
}

export const listDeregisterQuestions = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  const { data } = await httpClient.get<DeregisterQuestion[]>(
    '/auth/user/deregister/questions',
  )
  return data
}

export const deregister = async (
  { httpClient }: { httpClient: HttpClient },
  params: DeregisterParams,
) => {
  return await httpClient.post('/auth/user/deregister', params)
}

export const createPasskeyRegisterOptions = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  const options: PasskeyRegistrationOptions = await httpClient.post(
    '/auth/user/passkey/register/options',
  )
  return options
}

export const registerPasskey = async (
  { httpClient }: { httpClient: HttpClient },
  credential: PasskeyCredential,
) => {
  await httpClient.post('/auth/user/passkey/register', credential)
}

export const createPasskeyLoginOptions = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  const options: PasskeyAuthenticationOptions = await httpClient.post(
    '/auth/user/passkey/login/options',
  )
  return options
}

export const loginWithPasskey = async (
  { httpClient }: { httpClient: HttpClient },
  credential: PasskeyCredential,
) => {
  const { data }: ApiResponse<AuthUser> = await httpClient.post(
    '/auth/user/passkey/login',
    credential,
  )
  return data
}

export const listPasskeys = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  const { data }: ApiResponse<Passkey[]> =
    await httpClient.get('/auth/user/passkeys')
  return data
}

export const deletePasskey = async (
  { httpClient }: { httpClient: HttpClient },
  id: number,
) => {
  await httpClient.delete(`/auth/user/passkeys/${id}`)
}

export const hidePasskeyPromotion = async ({
  httpClient,
}: {
  httpClient: HttpClient
}) => {
  await httpClient.put('/auth/user/passkey/promotion/hide')
}

export const directUserAuthForSocialLogin = async (
  { httpClient }: { httpClient: HttpClient },
  userSocialAuthenticationId: string,
  params: {
    expires: number
    signature: string
  },
) => {
  return await httpClient.get(
    `/user/direct_auth_for_social_login/${userSocialAuthenticationId}`,
    {
      params,
    },
  )
}
